import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getPaginatedWorkOrders, getWorkOrderById, updateWorkOrder } from '../api/work-orderAPI';
import { createFormData } from '../api/form-dataAPI';
import { createWorkOrderFormAction, deleteWorkOrderFormAction, getWorkOrderFormActionsByWorkOrderFormId } from '../api/work-order-form-actionsAPI';
import { getForms, getFormById } from '../api/formsAPI';
import {
  getWorkOrderForms,
  createWorkOrderForm,
  updateWorkOrderForm,
  deleteWorkOrderForm,
  getWorkOrderFormsByWorkOrderId
} from '../api/work-order-formsAPI';
import {
  getWorkPackages,
  resendWorkAssignmentEmail,
  resendWorkAssignmentSms,
  shareWorkPackage,
} from '../api/work-packagesAPI';
import { getUserses } from '../api/usersAPI';
import { getContractors } from '../api/contractorsAPI';
import { useTableFilters } from '../utils/useTableFilters';
import { showToastError, showToastSuccess } from '../utils/errorController';
import { toUtcIsoWithTime } from '../utils/dateUtils';
import { renderPreviewInput as previewRenderer } from '../utils/renderPreviewInput';
import { getUserProfile } from '../auth/authApi';
import WorkAssignmentsCalendarGrid from './WorkAssignmentsCalendarGrid';
import WorkAssignDrawer from './WorkAssignDrawer';
import AssigneeList from './AssigneeList';
import normaliseAssignments from './normaliseAssignments';
import WorkOrderDetails from './WorkOrderDetails';
import FormLivePreview from './FormLivePreview';
import { getFormInputType } from '../api/form-input-typeAPI';
import { getWorkOrderPartsByWorkOrderId } from '../api/work-order-partsAPI';
import { getBillOfEquipmentWosByWorkOrderId } from '../api/bill-of-equipment-woAPI';
import { getBillOfLabourWosByWorkOrderId } from '../api/bill-of-labour-woAPI';
import { getBillOfServicesWosByWorkOrderId } from '../api/bill-of-services-woAPI';
import { getTrades } from '../api/tradesAPI';
import { getExpenseCodes } from '../api/expense-codeAPI';
import { getExternalServices } from '../api/external-servicesAPI';
import { Icon } from '@iconify/react';
import { useFormBuilder } from '../utils/useFormBuilder';
import { create, update, deleteOne } from '../api';
import { saveWorkOrderParts } from './WorkOrderPartsTab';

const RANGE_OPTIONS = {
  week: 7,
  fortnight: 14,
  month: 30,
};

const toIsoDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
const toIsoDateTime = (date) => date.toISOString();

const profileImageUrl = (value) => {
  if (!value) return '';
  if (String(value).startsWith('http://') || String(value).startsWith('https://')) return value;
  return `${process.env.REACT_APP_SPACES_BASE}/${value}`;
}

const toUtcDateTime = (dateString, endOfDay = false) => {
  const [year, month, day] = (dateString || '').split('-').map(Number);
  if (!year || !month || !day) return null;

  const timestamp = endOfDay
    ? Date.UTC(year, month - 1, day, 23, 59, 59, 999)
    : Date.UTC(year, month - 1, day, 0, 0, 0, 0);

  return toIsoDateTime(new Date(timestamp));
};

const startOfDay = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const addDays = (date, count) => {
  const next = new Date(date);
  next.setDate(next.getDate() + count);
  return next;
};

const getDurationMs = (startAt, dueDate) => {
  if (!startAt || !dueDate) return null;
  const start = new Date(startAt);
  const end = new Date(dueDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const diff = end.getTime() - start.getTime();
  return diff > 0 ? diff : null;
};

const getDaysForWindow = (start, viewMode) => Array.from({ length: RANGE_OPTIONS[viewMode] || 7 }, (_, index) => toIsoDate(addDays(start, index)));

const safeArray = (value) => (Array.isArray(value) ? value : []);

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseDepartmentIds = (value) => {
  if (value === undefined || value === null || value === '') return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => toNumber(item))
      .filter((item) => item !== null);
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => toNumber(item)).filter((item) => item !== null);
      }
    } catch (_error) {
      return value.split(',').map((part) => toNumber(part.trim())).filter((item) => item !== null);
    }
  }

  const parsed = toNumber(value);
  return parsed === null ? [] : [parsed];
};

const hasDepartmentAccess = (departmentList, selectedDepartments) => {
  if (!selectedDepartments.length) return false;
  if (selectedDepartments.includes(-1) || departmentList.includes(-1)) return true;
  return departmentList.some((id) => selectedDepartments.includes(id));
};

const filterAssignments = ({ source, selectedDepartmentIds, typeFilter, assignmentTypeFilter }) => source.filter((event) => {
  const eventDepartmentIds = parseDepartmentIds(
    event?.original?.departmentIds
    || event?.original?.departmentId
    || event?.original?.department?.id,
  );

  if (selectedDepartmentIds !== null && !hasDepartmentAccess(eventDepartmentIds, selectedDepartmentIds)) return false;

  if (typeFilter === 'workOrder' && event.kind !== 'workOrder') return false;
  if (typeFilter === 'form' && event.kind !== 'form') return false;
  if (typeFilter === 'assigned' && event.assignees.length === 0) return false;
  if (typeFilter === 'notAssigned' && event.assignees.length > 0) return false;

  if (assignmentTypeFilter === 'internal' || assignmentTypeFilter === 'contractor') {
    const desiredType = assignmentTypeFilter === 'internal' ? 'internal' : 'contractor';
    const filteredAssignees = event.assignees.filter((a) => a.type === desiredType);
    if (!filteredAssignees.length) return false;
    event.assignees = filteredAssignees;
  }

  return true;
});

const WorkAssignmentLayer = () => {
  const [viewMode, setViewMode] = useState('week');
  const [windowStart, setWindowStart] = useState(startOfDay(new Date()));
  const [loading, setLoading] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [forms, setForms] = useState([]);
  const [crew, setCrew] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [searchAssignee, setSearchAssignee] = useState('');
  const [showDrawer, setShowDrawer] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [drawerContext, setDrawerContext] = useState(null);
  const [quickAssignMode, setQuickAssignMode] = useState('workOrder');
  const [listCollapsedGroups, setListCollapsedGroups] = useState({ internal: true, contractor: false });
  const [calendarCollapsedGroups, setCalendarCollapsedGroups] = useState({ internal: false, contractor: false });
  const [currentUserDepartmentIds, setCurrentUserDepartmentIds] = useState([]);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [assigneeVisibility, setAssigneeVisibility] = useState({});
  const [pendingScheduleChange, setPendingScheduleChange] = useState(null);
  const [updatingSchedule, setUpdatingSchedule] = useState(false);
  const [pendingAssignmentRemoval, setPendingAssignmentRemoval] = useState(null);
  const [removingAssignment, setRemovingAssignment] = useState(false);
  const [showWorkOrderDetailsModal, setShowWorkOrderDetailsModal] = useState(false);
  const [workOrderDetailsLoading, setWorkOrderDetailsLoading] = useState(false);
  const [savingWorkOrderEdit, setSavingWorkOrderEdit] = useState(false);
  const [workOrderPreviewTab, setWorkOrderPreviewTab] = useState('general');
  const [workOrderDetails, setWorkOrderDetails] = useState(null);
  const [workOrderParts, setWorkOrderParts] = useState([]);
  const [workOrderEquipment, setWorkOrderEquipment] = useState([]);
  const [workOrderLabour, setWorkOrderLabour] = useState([]);
  const [workOrderServices, setWorkOrderServices] = useState([]);
  const [workOrderLinkedForms, setWorkOrderLinkedForms] = useState([]);
  const [deletedPartIds, setDeletedPartIds] = useState([]);
  const [deletedLabourIds, setDeletedLabourIds] = useState([]);
  const [deletedEquipmentIds, setDeletedEquipmentIds] = useState([]);
  const [deletedServiceIds, setDeletedServiceIds] = useState([]);
  const [expenseOptions, setExpenseOptions] = useState([]);
  const [tradeOptions, setTradeOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [showFormPreviewModal, setShowFormPreviewModal] = useState(false);
  const [formPreviewLoading, setFormPreviewLoading] = useState(false);
  const [previewForm, setPreviewForm] = useState(null);
  const [formPreviewTab, setFormPreviewTab] = useState('general');
  const [formGeneralData, setFormGeneralData] = useState({});
  const [formGeneralSaving, setFormGeneralSaving] = useState(false);
  const [resendingCommunication, setResendingCommunication] = useState({ sms: false, email: false });
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);
  const [formInputTypes, setFormInputTypes] = useState([]);
  const gridRef = useRef(null);
  useEffect(() => {
    setHeaderSearchContainer(document.getElementById('header-search-container'));
  }, []);

  const days = useMemo(() => getDaysForWindow(windowStart, viewMode), [windowStart, viewMode]);
  const windowEnd = days[days.length - 1];
  const calendarStartDateTime = useMemo(() => toUtcDateTime(days[0]) || '', [days]);
  const calendarEndDateTime = useMemo(() => toUtcDateTime(windowEnd, true) || '', [windowEnd]);

  const tableFilters = useMemo(() => ([
    {
      field: 'departmentId',
      label: 'Departments',
      type: 'dropDown',
      api: 'hierarchy-structures',
      displayField: 'name',
      isMulti: true,
      menuBarFilter: 1,
    },
    {
      field: 'type',
      label: 'Types',
      type: 'dropDown',
      isMulti: false,
      menuBarFilter: 1,
      options: [
        { value: 'all', label: 'All' },
        { value: 'workOrder', label: 'Work Orders' },
        { value: 'form', label: 'Forms' },
        { value: 'assigned', label: 'Assigned' },
        { value: 'notAssigned', label: 'Not Assigned' },
      ],
    },
    {
      field: 'assignmentType',
      label: 'Assignments',
      type: 'dropDown',
      isMulti: false,
      menuBarFilter: 1,
      options: [
        { value: 'all', label: 'All Assignments' },
        { value: 'internal', label: 'Crew' },
        { value: 'contractor', label: 'Contractors' },
      ],
    },
    {
      field: 'assetClassId',
      label: 'Asset Class',
      type: 'dropDown',
      api: 'class',
      displayField: 'className',
      isMulti: true,
      menuBarFilter: 1,
    },
  ]), []);

  const {
    tempFilters,
    appliedFilterParams,
    resetFilters,
    renderMenuBarFilters,
  } = useTableFilters(tableFilters, {
    departmentId: [],
    type: 'all',
    assignmentType: 'all'
  });

  const fetchCurrentUserProfile = useCallback(async () => {
    try {
      const profile = await getUserProfile();
      const departments = parseDepartmentIds(profile?.departments);
      const canAccessAllDepartments = departments.includes(-1);
      setCurrentUserProfile(profile || null);
      if (canAccessAllDepartments) {
        setCurrentUserDepartmentIds([-1]);
      } else {
        setCurrentUserDepartmentIds(departments);
      }
    } catch (error) {
      console.error('Failed loading user profile for department access', error);
      setCurrentUserProfile(null);
      setCurrentUserDepartmentIds([]);
    }
  }, []);

  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    try {
      const selectedDepartmentParam = Array.isArray(appliedFilterParams.departmentId)
        ? appliedFilterParams.departmentId
        : [];
      const requestedDepartmentIds = selectedDepartmentParam.length
        ? selectedDepartmentParam
        : currentUserDepartmentIds.includes(-1)
          ? []
          : currentUserDepartmentIds;

      const [woResp, formResp, formsCatalogResp, usersResp, contractorsResp] = await Promise.all([
        getPaginatedWorkOrders({
          page: 1,
          pageSize: 1000,
          departmentId: requestedDepartmentIds,
          statusId: [1, 2],
          ...appliedFilterParams,
          scheduledAtStart: calendarStartDateTime,
          scheduledAtEnd: calendarEndDateTime,
          requireAssignees: false,
        }),
        getWorkOrderForms(),
        getForms(),
        getUserses(),
        getContractors(),
      ]);

      const filteredWos = safeArray(woResp.data);

      const filteredForms = safeArray(formResp).filter((item) => {
        const d = toIsoDate(item?.scheduledAt || item?.assignedAt);
        return d && d >= days[0] && d <= windowEnd;
      });

      setWorkOrders(filteredWos);
      setForms(Object.assign(filteredForms, { _catalog: safeArray(formsCatalogResp?.data || formsCatalogResp) }));
      setCrew(safeArray(usersResp));
      setContractors(safeArray(contractorsResp));
    } catch (error) {
      console.error('Failed loading assignments calendar', error);
    } finally {
      setLoading(false);
    }
  }, [days, windowEnd, appliedFilterParams.departmentId, currentUserDepartmentIds, calendarStartDateTime, calendarEndDateTime]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  useEffect(() => {
    fetchCurrentUserProfile();
  }, [fetchCurrentUserProfile]);

  useEffect(() => {
    const loadFormInputTypes = async () => {
      try {
        const response = await getFormInputType();
        setFormInputTypes(safeArray(response?.data || response));
      } catch (error) {
        console.error('Failed loading form input types', error);
        setFormInputTypes([]);
      }
    };

    loadFormInputTypes();
  }, []);

  const selectedDepartmentIds = useMemo(() => {
    const selected = parseDepartmentIds(appliedFilterParams.departmentId);
    if (selected.length) return selected;
    if (currentUserDepartmentIds.includes(-1)) return null;
    return currentUserDepartmentIds;
  }, [appliedFilterParams.departmentId, currentUserDepartmentIds]);

  const crewById = useMemo(() => {
    const map = {};
    crew.forEach((member) => { map[member.id] = member; });
    return map;
  }, [crew]);

  const contractorById = useMemo(() => {
    const map = {};
    contractors.forEach((item) => { map[item.id] = item; });
    return map;
  }, [contractors]);

  const events = useMemo(() => {
    const source = normaliseAssignments({ workOrders, forms, crewById, contractorById });
    return filterAssignments({
      source,
      selectedDepartmentIds,
      typeFilter: tempFilters.type,
      assignmentTypeFilter: tempFilters.assignmentType,
    });
  }, [workOrders, forms, crewById, contractorById, tempFilters, selectedDepartmentIds]);

  const assignmentUniverse = useMemo(() => {
    const source = normaliseAssignments({ workOrders, forms, crewById, contractorById });
    return filterAssignments({
      source,
      selectedDepartmentIds,
      typeFilter: 'all',
      assignmentTypeFilter: tempFilters.assignmentType,
    });
  }, [workOrders, forms, crewById, contractorById, selectedDepartmentIds, tempFilters.assignmentType]);

  const contractorIdsWithAssignments = useMemo(() => {
    const ids = new Set();
    events.forEach((event) => {
      event.assignees.forEach((assignee) => {
        if (assignee.type === 'contractor') ids.add(Number(assignee.id));
      });
    });
    return ids;
  }, [events]);

  const assigneeRows = useMemo(() => {
    const internal = crew
      .filter((member) => Number(member?.assignable) !== 0)
      .filter((member) => selectedDepartmentIds === null || hasDepartmentAccess(parseDepartmentIds(member?.departments), selectedDepartmentIds))
      .map((member) => ({
        id: member.id,
        name: member.name || member.email || `User ${member.id}`,
        profileImage: profileImageUrl(member.userImage),
        type: 'internal',
      }));
    const external = contractors.map((member) => ({
      id: member.id,
      name: member.name || `Contractor ${member.id}`,
      secondaryName: member?.supplier?.name || '',
      profileImage: profileImageUrl(member.profileImage),
      type: 'contractor',
    }));
    const q = searchAssignee.trim().toLowerCase();
    const applySearch = (rows) => (!q ? rows : rows.filter((row) => row.name.toLowerCase().includes(q)));

    const visibleInternal = applySearch(internal).filter((row) => assigneeVisibility[`internal:${row.id}`] !== false);
    const visibleExternal = applySearch(external).filter((row) => assigneeVisibility[`contractor:${row.id}`] !== false);

    const hasUnassignedEvents = events.some((event) => event.assignees.length === 0);

    const visibleByAssignmentType = (() => {
      if (tempFilters.assignmentType === 'internal') return { internal: visibleInternal, contractor: [] };
      if (tempFilters.assignmentType === 'contractor') return { internal: [], contractor: visibleExternal };
      return { internal: visibleInternal, contractor: visibleExternal };
    })();

    const eventKeysByAssignee = new Set();
    assignmentUniverse.forEach((event) => {
      event.assignees.forEach((assignee) => {
        eventKeysByAssignee.add(`${assignee.type}:${assignee.id}`);
      });
    });

    const assignmentStateFilteredInternal = tempFilters.type === 'assigned'
      ? visibleByAssignmentType.internal.filter((row) => eventKeysByAssignee.has(`internal:${row.id}`))
      : tempFilters.type === 'notAssigned'
        ? visibleByAssignmentType.internal.filter((row) => !eventKeysByAssignee.has(`internal:${row.id}`))
        : visibleByAssignmentType.internal;

    const assignmentStateFilteredExternal = tempFilters.type === 'assigned'
      ? visibleByAssignmentType.contractor.filter((row) => eventKeysByAssignee.has(`contractor:${row.id}`))
      : tempFilters.type === 'notAssigned'
        ? visibleByAssignmentType.contractor.filter((row) => !eventKeysByAssignee.has(`contractor:${row.id}`))
        : visibleByAssignmentType.contractor;

    const includeUnassignedRow = hasUnassignedEvents && tempFilters.type !== 'assigned' && tempFilters.assignmentType !== 'internal';

    const unassignedRows = includeUnassignedRow
      ? Array.from(
        events
          .filter((event) => event.kind === 'workOrder' && event.assignees.length === 0)
          .reduce((map, event) => {
            const taskTypeName = event?.original?.taskType?.taskTypeName || 'Unspecified Task Type';
            if (!map.has(taskTypeName)) {
              map.set(taskTypeName, {
                id: taskTypeName,
                name: taskTypeName,
                type: 'unassigned',
              });
            }
            return map;
          }, new Map())
          .values(),
      )
      : [];

    return {
      internal: applySearch(internal),
      contractor: applySearch(external),
      unassigned: unassignedRows,
      flat: [...assignmentStateFilteredInternal, ...assignmentStateFilteredExternal, ...unassignedRows],
    };
  }, [crew, contractors, searchAssignee, assigneeVisibility, selectedDepartmentIds, events, assignmentUniverse, tempFilters.assignmentType, tempFilters.type]);

  useEffect(() => {
    setAssigneeVisibility((prev) => {
      const next = { ...prev };
      crew.forEach((member) => {
        const key = `internal:${member.id}`;
        if (!(key in next)) next[key] = true;
      });
      contractors.forEach((member) => {
        const key = `contractor:${member.id}`;
        if (!(key in next)) next[key] = contractorIdsWithAssignments.has(Number(member.id));
      });
      return next;
    });
  }, [crew, contractors, contractorIdsWithAssignments]);

  const eventsByCell = useMemo(() => {
    const map = {};
    events.forEach((event) => {
      if (event.kind === 'workOrder' && event.assignees.length === 0) {
        const taskTypeName = event?.original?.taskType?.taskTypeName || 'Unspecified Task Type';
        const key = `unassigned:${taskTypeName}:${event.date}`;
        if (!map[key]) map[key] = [];
        map[key].push(event);
      }

      event.assignees.forEach((assignee) => {
        if (assigneeVisibility[`${assignee.type}:${assignee.id}`] === false) return;
        const key = `${assignee.type}:${assignee.id}:${event.date}`;
        if (!map[key]) map[key] = [];
        map[key].push(event);
      });
    });
    return map;
  }, [events, assigneeVisibility]);

  const openDrawer = (context = {}, mode = quickAssignMode) => {
    setQuickAssignMode(mode);
    setDrawerContext(context);
    setShowDrawer(true);
  };

  const shiftWindow = (direction) => {
    setWindowStart((prev) => addDays(prev, direction * (RANGE_OPTIONS[viewMode] || 7)));
  };

  const handleToggleGroupVisibility = useCallback((groupKey, nextVisible) => {
    const rows = assigneeRows[groupKey] || [];
    setAssigneeVisibility((prev) => {
      const next = { ...prev };
      rows.forEach((assignee) => {
        next[`${assignee.type}:${assignee.id}`] = nextVisible;
      });
      return next;
    });
  }, [assigneeRows]);

  const restoreGridScroll = useCallback((scrollPosition) => {
    if (!scrollPosition || !gridRef.current) return;
    requestAnimationFrame(() => {
      if (!gridRef.current) return;
      gridRef.current.scrollTop = scrollPosition.top;
      gridRef.current.scrollLeft = scrollPosition.left;
    });
  }, []);

  const handleOpenWorkOrderDetails = useCallback(async (event) => {
    const workOrderId = event?.original?.id || event?.original?.workOrderId;
    if (!workOrderId) return;

    setShowWorkOrderDetailsModal(true);
    setWorkOrderPreviewTab('general');
    setWorkOrderDetailsLoading(true);
    try {
      const [
        workOrder,
        parts,
        equipment,
        labour,
        services,
        formsForWorkOrder,
        trades,
        expenses,
        externalServices,
      ] = await Promise.all([
        getWorkOrderById(workOrderId),
        getWorkOrderPartsByWorkOrderId(workOrderId),
        getBillOfEquipmentWosByWorkOrderId(workOrderId),
        getBillOfLabourWosByWorkOrderId(workOrderId),
        getBillOfServicesWosByWorkOrderId(workOrderId),
        getWorkOrderFormsByWorkOrderId(workOrderId),
        getTrades(),
        getExpenseCodes(),
        getExternalServices(),
      ]);
      setWorkOrderDetails(workOrder || null);
      setWorkOrderParts(safeArray(parts));
      setWorkOrderEquipment(safeArray(equipment));
      setWorkOrderLabour(safeArray(labour));
      setWorkOrderServices(safeArray(services));
      setWorkOrderLinkedForms(safeArray(formsForWorkOrder));
      setTradeOptions(safeArray(trades));
      setExpenseOptions(safeArray(expenses));
      setServiceOptions(safeArray(externalServices));
    } catch (error) {
      console.error('Failed loading work order details', error);
      showToastError('Unable to open work order details');
      setShowWorkOrderDetailsModal(false);
    } finally {
      setWorkOrderDetailsLoading(false);
    }
  }, []);

  const handleCloseWorkOrderDetails = useCallback(() => {
    setShowWorkOrderDetailsModal(false);
    setWorkOrderPreviewTab('general');
    setWorkOrderDetails(null);
    setWorkOrderParts([]);
    setWorkOrderEquipment([]);
    setWorkOrderLabour([]);
    setWorkOrderServices([]);
    setWorkOrderLinkedForms([]);
    setDeletedPartIds([]);
    setDeletedLabourIds([]);
    setDeletedEquipmentIds([]);
    setDeletedServiceIds([]);
    setExpenseOptions([]);
    setTradeOptions([]);
    setServiceOptions([]);
    setResendingCommunication({ sms: false, email: false });
  }, []);

  const handleOpenFormPreview = useCallback(async (event) => {
    const formId = event?.original?.formId;
    if (!formId) {
      showToastError('Unable to find form template for this assignment');
      return;
    }

    setShowFormPreviewModal(true);
    setFormPreviewLoading(true);
    try {
      const form = await getFormById(formId);
      const previewItems = safeArray(
        event?.original?.tblFormItems
        || event?.original?.formItems
        || event?.original?.items
        || form?.tblFormItems
        || form?.items,
      );
      const hydratedForm = {
        ...event?.original,
        formTemplate: form || null,
        tblFormItems: previewItems,
        items: previewItems,
        formName: event?.original?.formName || form?.name || '',
      };
      setPreviewForm(hydratedForm || null);
      setFormGeneralData({
        scheduledAt: event?.original?.scheduledAt || '',
        duration: event?.original?.duration ?? 0,
        meetingPoint: event?.original?.meetingPoint || '',
        locationAddress: event?.original?.locationAddress || '',
        reportingTo: event?.original?.reportingTo || event?.original?.reportTo || '',
      });
      setFormPreviewTab('general');
    } catch (error) {
      console.error('Failed loading form preview', error);
      showToastError('Unable to open form preview');
      setShowFormPreviewModal(false);
    } finally {
      setFormPreviewLoading(false);
    }
  }, []);

  const handleCloseFormPreview = useCallback(() => {
    setShowFormPreviewModal(false);
    setPreviewForm(null);
    setFormGeneralData({});
    setFormPreviewTab('general');
    setResendingCommunication({ sms: false, email: false });
  }, []);

  const getContactDetails = useCallback((person) => {
    if (!person) return { phone: '', email: '' };
    return {
      phone: String(
        person.phoneE164
        || person.phone
        || person.user_phone
        || person.phoneNumber
        || person.mobile
        || '',
      ).trim(),
      email: String(person.email || person.userEmail || '').trim(),
    };
  }, []);

  const resolveWorkOrderAssignedContractor = useMemo(() => {
    const candidateIds = safeArray(workOrderDetails?.contractors).map((item) => Number(typeof item === 'object' ? item?.id : item)).filter(Number.isFinite);
    const matched = contractors.find((member) => candidateIds.includes(Number(member?.id)));
    return matched || null;
  }, [contractors, workOrderDetails?.contractors]);

  const resolveFormAssignedContractor = useMemo(() => {
    const assignedContractorId = Number(previewForm?.assignedContractorId || previewForm?.assignedContractor?.id);
    if (Number.isFinite(assignedContractorId)) {
      return contractors.find((member) => Number(member?.id) === assignedContractorId) || previewForm?.assignedContractor || null;
    }
    return previewForm?.assignedContractor || null;
  }, [contractors, previewForm]);

  const workOrderCommunication = useMemo(() => getContactDetails(resolveWorkOrderAssignedContractor), [getContactDetails, resolveWorkOrderAssignedContractor]);
  const formCommunication = useMemo(() => getContactDetails(resolveFormAssignedContractor), [getContactDetails, resolveFormAssignedContractor]);

  const handleResendCommunication = useCallback(async ({ channel, kind }) => {
    const isSms = channel === 'sms';
    const communication = kind === 'workOrder' ? workOrderCommunication : formCommunication;
    const assignedContractor = kind === 'workOrder'
      ? resolveWorkOrderAssignedContractor
      : resolveFormAssignedContractor;
    const target = isSms ? communication.phone : communication.email;
    if (!target) return;

    setResendingCommunication((prev) => ({ ...prev, [channel]: true }));
    try {
      const tenantId = Number(currentUserProfile?.tenantId);
      const globalContractorId = Number(assignedContractor?.globalId);
      if (!Number.isFinite(tenantId) || !Number.isFinite(globalContractorId)) {
        throw new Error('Missing contractor or tenant context for resend');
      }

      console.log('globalContractorId', globalContractorId);
      console.log('tenantId', tenantId);


      const options = await getWorkPackages({ globalContractorId, tenantId });
      const existing = Array.isArray(options?.data) ? options.data[0] : Array.isArray(options) ? options[0] : null;
      const workPackageId = Number(existing?.id);
      if (!Number.isFinite(workPackageId)) {
        throw new Error('No work package found for contractor');
      }

      if (isSms) {
        await resendWorkAssignmentSms({
          to: target,
          workPackageId,
        });
      } else {
        await resendWorkAssignmentEmail({
          to: target,
          workPackageId,
        });
      }
      showToastSuccess(`${isSms ? 'SMS' : 'Email'} resent successfully`);
    } catch (error) {
      console.error('Failed to resend assignment communication', error);
      showToastError(`Unable to resend ${isSms ? 'SMS' : 'email'}`);
    } finally {
      setResendingCommunication((prev) => ({ ...prev, [channel]: false }));
    }
  }, [
    currentUserProfile?.tenantId,
    formCommunication,
    resolveFormAssignedContractor,
    resolveWorkOrderAssignedContractor,
    workOrderCommunication,
  ]);

  const renderCommunicationOptions = useCallback(({
    communication,
    kind,
    showSms,
  }) => (
    <div className='border rounded-3 p-3 bg-primary-50 mt-8'>
      <div className='d-flex flex-column gap-1 mb-3'>
        <div className='fw-semibold'>Communication Options</div>
      </div>

      {showSms && (
        <div className='d-flex align-items-center justify-content-between rounded p-3 bg-white border mb-16'>
          <div className='d-flex flex-column pe-3'>
            <span className='fw-medium'>SMS</span>
            <span className='text-muted small'>{communication.phone || 'No phone number recorded'}</span>
          </div>
          <button
            type='button'
            className='btn btn-sm btn-outline-primary'
            disabled={!communication.phone || resendingCommunication.sms}
            onClick={() => handleResendCommunication({ channel: 'sms', kind })}
          >
            {resendingCommunication.sms ? 'Resending...' : 'Resend'}
          </button>
        </div>
      )}

      <div className='d-flex align-items-center justify-content-between rounded p-3 bg-white border'>
        <div className='d-flex flex-column pe-3'>
          <span className='fw-medium'>Email</span>
          <span className='text-muted small'>{communication.email || 'No email address recorded'}</span>
        </div>
        <button
          type='button'
          className='btn btn-sm btn-outline-primary'
          disabled={!communication.email || resendingCommunication.email}
          onClick={() => handleResendCommunication({ channel: 'email', kind })}
        >
          {resendingCommunication.email ? 'Resending...' : 'Resend'}
        </button>
      </div>
    </div>
  ), [handleResendCommunication, resendingCommunication.email, resendingCommunication.sms]);

  const formGeneralFields = useMemo(() => ([
    { field: 'foNumber', label: 'Form Number', type: 'text', size: 6, disabled: true },
    { field: 'formName', label: 'Form Name', type: 'text', size: 6, disabled: true },
    { field: 'assetSummary', label: 'Asset', type: 'text', size: 12, disabled: true },
    { field: 'departmentName', label: 'Department', type: 'text', size: 6, disabled: true },
    { field: 'assignedAt', label: 'Assigned At', type: 'datetime', size: 6, disabled: true },
    { field: 'scheduledAt', label: 'Scheduled At', type: 'datetime', size: 6 },
    { field: 'duration', label: 'Duration', type: 'number', size: 6, step: '0.01' },
    { field: 'meetingPoint', label: 'Meeting Point', type: 'text', size: 6 },
    { field: 'reportingTo', label: 'Reporting To', type: 'text', size: 6 },
    { field: 'locationAddress', label: 'Location Address', type: 'text', size: 12 },

  ]), []);

  const editWorkOrderFields = useMemo(() => ([
    { field: 'workOrderNumber', label: 'Work Order Number', type: 'text', size: 6, disabled: true },
    { field: 'workOrderTitle', label: 'Title', type: 'text', size: 6, required: true },
    { field: 'scheduledAt', label: 'Scheduled At', type: 'datetime', size: 6 },
    { field: 'dueDate', label: 'Due Date', type: 'datetime', size: 6 },
    { field: 'meetingPoint', label: 'Meeting Point', type: 'text', size: 6 },
    { field: 'reportingTo', label: 'Reporting To', type: 'text', size: 6 },
    { field: 'locationAddress', label: 'Location', type: 'text', size: 12 },
    { field: 'workOrderDescription', label: 'Description', type: 'textarea', size: 12 },
  ]), []);

  const editWorkOrderData = useMemo(() => ({
    ...workOrderDetails,
    scheduledAt: workOrderDetails?.scheduledAt || '',
    dueDate: workOrderDetails?.dueDate || '',
  }), [workOrderDetails]);

  const { renderField: renderEditWorkOrderField } = useFormBuilder(
    editWorkOrderFields,
    editWorkOrderData,
    (payload) => {
      if (typeof payload === 'function') {
        setWorkOrderDetails((prev) => (prev ? payload(prev) : prev));
      } else {
        setWorkOrderDetails((prev) => ({ ...(prev || {}), ...(payload || {}) }));
      }
    },
    {},
  );

  const handleSaveEditedWorkOrder = useCallback(async () => {
    if (!workOrderDetails?.id) return;
    setSavingWorkOrderEdit(true);
    try {
      const woData = { ...workOrderDetails };
      const cleanPayload = {};
      for (const key in woData) {
        const value = woData[key];
        const isTaskField = key === 'taskId' || key === 'cycleGroupOrder';
        if (
          key !== 'workOrderScheduleId'
          && typeof value !== 'object'
          && (value !== null || isTaskField)
        ) {
          cleanPayload[key] = value;
        }
      }

      const { partsCount, ...cleanPayloadWithoutPartsCount } = cleanPayload;

      const payload = {
        ...cleanPayloadWithoutPartsCount,
        workOrderScheduleId: woData.workOrderScheduleId,
        scheduledAt: woData.scheduledAt ? toUtcIsoWithTime(woData.scheduledAt) : null,
        dueDate: woData.dueDate ? toUtcIsoWithTime(woData.dueDate) : null,
        startedAt: woData.startedAt ? toUtcIsoWithTime(woData.startedAt) : null,
        completedAt: woData.completedAt ? toUtcIsoWithTime(woData.completedAt) : null,
        taskCodeId: woData.taskCodeId,
        taskId: woData.taskId,
        cycleGroupOrder: woData.cycleGroupOrder ?? null,
        crew: woData.crew,
        workorderFiles: woData.workorderFiles,
        workOrderImage: woData.workOrderImage,
        completionImages: woData.completionImages,
        departmentId: woData.departmentId,
        eventId: woData.eventId || null,
        organisationId: currentUserProfile?.org_id,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUserProfile?.id,
      };

      await updateWorkOrder(workOrderDetails.id, payload);

      const syncedParts = await saveWorkOrderParts(workOrderDetails.id, workOrderParts);
      setWorkOrderParts(syncedParts);

      await Promise.all(deletedPartIds.map((id) => deleteOne('work-order-parts', id)));
      await Promise.all(deletedLabourIds.map((id) => deleteOne('bill-of-labour-wo', id)));
      await Promise.all(deletedEquipmentIds.map((id) => deleteOne('bill-of-equipment-wos', id)));
      await Promise.all(deletedServiceIds.map((id) => deleteOne('bill-of-services-wo', id)));

      const equipmentUpdates = workOrderEquipment.map((item) => (
        item.id
          ? update('bill-of-equipment-wos', item.id, item)
          : create('bill-of-equipment-wos', { ...item, workOrderId: workOrderDetails.id })
      ));
      await Promise.all(equipmentUpdates);

      const labourUpdates = workOrderLabour.map((item) => (
        item.id
          ? update('bill-of-labour-wo', item.id, item)
          : create('bill-of-labour-wo', { ...item, workOrderId: workOrderDetails.id })
      ));
      await Promise.all(labourUpdates);

      const serviceUpdates = workOrderServices.map((item) => {
        const servicePayload = { ...item };
        delete servicePayload.purchaseOrderNumber;
        delete servicePayload.poNumber;
        return servicePayload.id
          ? update('bill-of-services-wo', servicePayload.id, servicePayload)
          : create('bill-of-services-wo', { ...servicePayload, workOrderId: workOrderDetails.id });
      });
      await Promise.all(serviceUpdates);

      showToastSuccess('Work order updated successfully');
      handleCloseWorkOrderDetails();
      await fetchCalendarData();
    } catch (error) {
      console.error('Failed updating work order', error);
      showToastError('Failed to update work order');
    } finally {
      setSavingWorkOrderEdit(false);
    }
  }, [
    currentUserProfile?.id,
    currentUserProfile?.org_id,
    deletedEquipmentIds,
    deletedLabourIds,
    deletedPartIds,
    deletedServiceIds,
    fetchCalendarData,
    workOrderDetails,
    workOrderEquipment,
    workOrderLabour,
    workOrderParts,
    workOrderServices,
    handleCloseWorkOrderDetails,
  ]);

  const formGeneralDisplayData = useMemo(() => ({
    foNumber: previewForm?.id ? `FO-${String(previewForm.id).padStart(8, '0')}` : '',
    formName: previewForm?.formName || previewForm?.name || '',
    assetSummary: previewForm?.assetId
      ? `${previewForm?.asset?.assetNumber || ''} - ${previewForm?.asset?.assetDescription || ''}`.trim()
      : '',
    departmentName: previewForm?.department?.name || '',
    assignedAt: previewForm?.assignedAt || '',
    ...formGeneralData,
  }), [formGeneralData, previewForm]);

  const { renderField: renderFormGeneralField } = useFormBuilder(formGeneralFields, formGeneralDisplayData, setFormGeneralData, {});

  const handleSaveFormGeneral = useCallback(async () => {
    if (!previewForm?.id) return;
    setFormGeneralSaving(true);
    try {
      await updateWorkOrderForm(previewForm.id, {
        scheduledAt: formGeneralData?.scheduledAt || null,
        duration: Number(formGeneralData?.duration || 0),
        meetingPoint: formGeneralData?.meetingPoint || '',
        locationAddress: formGeneralData?.locationAddress || '',
        reportingTo: formGeneralData?.reportingTo || '',
      });
      showToastSuccess('Form details updated');
      await fetchCalendarData();
      handleCloseFormPreview();
    } catch (error) {
      console.error('Failed updating form details', error);
      showToastError('Unable to update form details');
    } finally {
      setFormGeneralSaving(false);
    }
  }, [fetchCalendarData, formGeneralData, handleCloseFormPreview, previewForm?.id]);

  const getInputType = useCallback((id) => formInputTypes.find((item) => Number(item.id) === Number(id)) || null, [formInputTypes]);

  const renderPreviewInput = useCallback((item) => previewRenderer(item, getInputType), [getInputType]);

  const ensureWorkAssignmentForContractor = async ({
    globalContractorId,
    workOrderId,
    workOrderNumber,
    formId,
    workOrderFormId,
    title,
    assignmentDetails,
    selectedDepartment,
    selectedAssetId,
    selectedAssetName,
    communicationOptions,
  }) => {
    const tenantId = Number(currentUserProfile?.tenantId);
    const options = await getWorkPackages({ globalContractorId, tenantId });
    const existing = Array.isArray(options?.data) ? options.data[0] : Array.isArray(options) ? options[0] : null;

    const assignmentPayload = {
      workOrderId: workOrderId || null,
      workOrderNumber: workOrderNumber || null,
      formId: formId || null,
      workOrderFormId: workOrderFormId || null,
      globalContractorId,
      reportTo: assignmentDetails.reportingTo || assignmentDetails.reportTo || '',
      startAt: assignmentDetails.startAt,
      duration: Number(assignmentDetails.duration || 0),
      expiryDate: assignmentDetails.expiryDate,
      meetingPoint: assignmentDetails.meetingPoint || '',
      location: assignmentDetails.locationAddress || assignmentDetails.location || '',
      instructions: assignmentDetails.instructions || '',
      departmentId: selectedDepartment?.id || null,
      departmentName: selectedDepartment?.name || null,
      assetId: selectedAssetId || null,
      assetName: selectedAssetName || null,
      selectedWorkPackageId: existing?.id || null,
      title,
      communication: {
        smsEnabled: Boolean(communicationOptions?.smsEnabled && communicationOptions?.phone),
        smsTo: communicationOptions?.phone || null,
        emailEnabled: Boolean(communicationOptions?.emailEnabled && communicationOptions?.email),
        emailTo: communicationOptions?.email || null,
      },
    };

    console.log('assignmentPayload',assignmentPayload);

    return shareWorkPackage(assignmentPayload);
  };

  const handleConfirmAssign = async ({
    kind,
    selectedItemId,
    crewIds,
    contractorIds,
    details,
    selectedWorkOrder,
    selectedForm,
    assignmentDetails,
    selectedAsset,
    selectedDepartment,
    selectedAssetId,
    selectedAssetName,
    selectedFormDetails,
    communicationOptions,
  }) => {
    const resolveSelectedStartAt = () => {
      const selectedStartAt = details?.startAt ? new Date(details.startAt) : null;
      return selectedStartAt && !Number.isNaN(selectedStartAt.getTime())
        ? toUtcIsoWithTime(selectedStartAt)
        : null;
    };

    if (!selectedItemId) {
      showToastError('Please select an item in step 3');
      return;
    }

    setSavingAssignment(true);

    try {

      if (kind === 'workOrder') {
        const selectedWorkOrderId = Number(selectedItemId);
        const fallbackWorkOrder = Number.isFinite(selectedWorkOrderId)
          ? workOrders.find((wo) => Number(wo.id) === selectedWorkOrderId)
          : null;
        const workOrder = selectedWorkOrder || fallbackWorkOrder || (Number.isFinite(selectedWorkOrderId)
          ? await getWorkOrderById(selectedWorkOrderId)
          : null);

        if (!workOrder?.id) {
          showToastError('Unable to find selected work order. Please reselect and try again.');
          return;
        }

        const existingCrew = safeArray(workOrder?.crew).map((item) => Number(typeof item === 'object' ? item?.id : item)).filter(Number.isFinite);
        const existingContractors = safeArray(workOrder?.contractors).map((item) => Number(typeof item === 'object' ? item?.id : item)).filter(Number.isFinite);

        const mergedCrew = Array.from(new Set([...existingCrew, ...crewIds.map(Number)]));
        const mergedContractors = Array.from(new Set([...existingContractors, ...contractorIds.map(Number)]));

        const selectedStartAt = resolveSelectedStartAt();
        const originalDurationMs = getDurationMs(workOrder?.scheduledAt, workOrder?.dueDate);
        const workOrderPayload = {
          crew: mergedCrew,
          contractors: mergedContractors,
          scheduledAt: selectedStartAt || (workOrder?.scheduledAt ? toUtcIsoWithTime(workOrder.scheduledAt) : null),
          meetingPoint: details?.meetingPoint || workOrder?.meetingPoint || '',
          locationAddress: details?.location || workOrder?.locationAddress || '',
        };

        if (selectedStartAt && originalDurationMs !== null) {
          const nextDueDate = new Date(new Date(selectedStartAt).getTime() + originalDurationMs);
          workOrderPayload.dueDate = toUtcIsoWithTime(nextDueDate);
        }

        await updateWorkOrder(workOrder.id, workOrderPayload);

        for (const contractorId of contractorIds) {
          const selectedContractor = contractors.find((contractor) => Number(contractor?.id) === Number(contractorId));
          const rawGlobalContractorId = selectedContractor?.globalId;

          if (rawGlobalContractorId === null || rawGlobalContractorId === undefined || rawGlobalContractorId === '') {
            console.warn('Skipping work package share for contractor without globalId', contractorId);
            continue;
          }

          const globalContractorId = Number(rawGlobalContractorId);
          if (!Number.isFinite(globalContractorId) || globalContractorId <= 0) {
            console.warn('Skipping work package share for contractor with invalid globalId', contractorId, rawGlobalContractorId);
            continue;
          }

          await ensureWorkAssignmentForContractor({
            globalContractorId,
            workOrderId: Number(workOrder.id),
            workOrderNumber: workOrder.workOrderNumber || null,
            formId: null,
            workOrderFormId: null,
            title: workOrder.workOrderTitle,
            assignmentDetails,
            selectedDepartment,
            selectedAssetId,
            selectedAssetName,
            communicationOptions,
          });
        }
      } else {
        // If internal user selected
        const selectedInternal = crewIds[0] || null;
        const userId = selectedInternal ?? null;
        const internalUserName = crew.find((person) => Number(person?.id) === Number(selectedInternal)) || null;
        // If contractor selected
        const selectedContractor = contractors.find((contractor) => Number(contractor?.id) === Number(contractorIds[0]));
        const globalContractorId = selectedContractor?.globalId;
        const contractorName = selectedContractor?.name || null;

        const assignedName = contractorName ?? internalUserName?.name;
        let newWorkOrderForm = null;
        const createdActionIds = [];
        const formDetails = await getFormById(selectedForm.id);
        const formId = formDetails.id;

        try {
          newWorkOrderForm = await createWorkOrderForm({
            formId: selectedForm.id,
            formName: selectedForm.name,
            workOrderId: null,
            assetId: selectedAssetId || selectedAsset?.id || null,
            departmentId: selectedDepartment?.id || null,
            locationId: 0,
            assignUserId: userId,
            assignedToName: assignedName,
            assignedContractorId: selectedContractor?.id || null,
            assignedAt: new Date().toISOString(),
            scheduled_at: assignmentDetails.startAt,
            scheduledAt: assignmentDetails.startAt,
            duration: assignmentDetails.duration,
            meetingPoint: assignmentDetails.meetingPoint || '',
            locationAddress: assignmentDetails.locationAddress || assignmentDetails.location || '',
            reportingTo: assignmentDetails.reportingTo || assignmentDetails.reportTo || '',
            assigned: 1,
            in_progress: 0,
            pdf_link: null,
            createdBy: userId,
          });

          const items = selectedFormDetails?.tblFormItems || formDetails?.tblFormItems || [];
          await Promise.all(items.map((item) => {
            const isAssetDetailsInput = Number(item.formInputTypeId) === 15;
            const selectedAssetLabel = selectedAsset
              ? `${selectedAsset.assetNumber || selectedAsset.unitNumber || ''} - ${selectedAsset.assetDescription || ''}`.trim()
              : '';

            return createFormData({
              formItemId: item.id,
              formId: item.formId,
              workOrderFormId: newWorkOrderForm.id,
              description: item.description,
              width: item.width,
              images: item.images,
              heading: item.heading,
              formInputTypeId: item.formInputTypeId,
              instructions: item.instructions,
              includePhoto: item.includePhoto,
              disableComments: item.disableComments,
              raiseDefects: item.raiseDefects,
              required: item.required,
              data: isAssetDetailsInput && selectedAssetLabel
                ? selectedAssetLabel
                : item.data || null,
              sortOrder: item.sortOrder,
              assetId: isAssetDetailsInput ? (selectedAsset?.id || null) : null,
              departmentId: null,
              teamId: null,
              createdBy: userId,
            });
          }));

          const actions = formDetails?.tblFormActions || [];

          await Promise.all(actions.map((action) => createWorkOrderFormAction({
            workOrderFormId: newWorkOrderForm.id,
            actionData: action.actionData,
            formActionId: action.formActionId,
            createdBy: userId,
          }).then((createdAction) => {
            if (createdAction?.id) createdActionIds.push(createdAction.id);
            return createdAction;
          })));

        } catch (error) {
          if (newWorkOrderForm?.id) {
            try {
              const actionIdsToDelete = createdActionIds.length > 0
                ? createdActionIds
                : ((await getWorkOrderFormActionsByWorkOrderFormId(newWorkOrderForm.id)) || []).map((action) => action?.id).filter((id) => Number.isFinite(Number(id)));
              if (actionIdsToDelete.length > 0) await Promise.all(actionIdsToDelete.map((id) => deleteWorkOrderFormAction(id)));
              await deleteWorkOrderForm(newWorkOrderForm.id);
            } catch (_cleanupError) {
              // noop
            }
          }
          throw error;
        }

        if (Number.isFinite(Number(globalContractorId)) && Number(globalContractorId) > 0) {
          await ensureWorkAssignmentForContractor({
            globalContractorId: Number(globalContractorId),
            workOrderId: null,
            formId,
            workOrderFormId: newWorkOrderForm?.id || null,
            title: selectedForm.name,
            assignmentDetails,
            selectedDepartment,
            selectedAssetId,
            selectedAssetName,
            communicationOptions,
          });
        }

        const shouldSendUserEmail = Boolean(
          communicationOptions?.type === 'user'
          && communicationOptions?.emailEnabled
          && communicationOptions?.email,
        );

        if (shouldSendUserEmail) {
          await create('work-packages/send-assignment-email', {
            to: communicationOptions.email,
            subject: 'Work assigned in Samurai',
            message: 'You have been assigned work in Samurai. Open https://app.samurai-cmms.com to review.',
          });
        }

      }

      setShowDrawer(false);
      showToastSuccess('Assignment saved successfully');
      await fetchCalendarData();
    } catch (error) {
      console.error('Failed to create assignment', error);
      showToastError('Failed to save assignment');
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleChipDrop = ({ dragEvent, day, assignee }) => {
    if (!dragEvent || !assignee || !day) return;
    const sourceAssignees = Array.isArray(dragEvent.assignees) ? dragEvent.assignees : [];
    const hasMatchingAssignee = sourceAssignees.some((item) => item?.type === assignee.type && Number(item?.id) === Number(assignee.id));
    if (!hasMatchingAssignee || dragEvent.date === day) return;

    const scrollPosition = gridRef.current
      ? { top: gridRef.current.scrollTop, left: gridRef.current.scrollLeft }
      : null;

    setPendingScheduleChange({ event: dragEvent, date: day, scrollPosition });
  };

  const handleConfirmScheduleChange = async () => {
    if (!pendingScheduleChange) return;

    const nextDate = pendingScheduleChange.date;
    const sourceEvent = pendingScheduleChange.event;
    const existingDateValue = sourceEvent?.original?.scheduledAt || sourceEvent?.original?.startDate || sourceEvent?.original?.assignedAt;
    const existingDate = existingDateValue ? new Date(existingDateValue) : null;
    const hasTime = existingDate && !Number.isNaN(existingDate.getTime());
    const targetDate = new Date(`${nextDate}T08:00:00`);

    if (hasTime) {
      targetDate.setHours(existingDate.getHours(), existingDate.getMinutes(), existingDate.getSeconds(), existingDate.getMilliseconds());
    }

    const scheduledAt = toUtcIsoWithTime(targetDate);

    setUpdatingSchedule(true);
    try {
      if (sourceEvent.kind === 'workOrder') {
        const workOrderId = sourceEvent?.original?.id || sourceEvent?.original?.workOrderId;
        if (!workOrderId) throw new Error('Missing work order id');
        const payload = { scheduledAt };
        await updateWorkOrder(workOrderId, payload);

      } else {
        const formId = sourceEvent?.original?.id || sourceEvent?.original?.formId;
        if (!formId) throw new Error('Missing form id');
        await updateWorkOrderForm(formId, { scheduledAt });
      }

      /* showToastSuccess('Scheduled date updated successfully');*/
      const previousScrollPosition = pendingScheduleChange.scrollPosition;
      setPendingScheduleChange(null);
      await fetchCalendarData();
      restoreGridScroll(previousScrollPosition);
    } catch (error) {
      console.error('Failed to reschedule assignment', error);
      showToastError('Failed to update scheduled date');
    } finally {
      setUpdatingSchedule(false);
    }
  };


  const handleRequestAssignmentRemoval = ({ event, assignee }) => {
    if (!event || !assignee) return;
    if (assignee.type === 'unassigned') return;
    setPendingAssignmentRemoval({ event, assignee });
  };

  const handleConfirmAssignmentRemoval = async () => {
    if (!pendingAssignmentRemoval) return;

    const { event, assignee } = pendingAssignmentRemoval;
    if (assignee?.type === 'unassigned') {
      setPendingAssignmentRemoval(null);
      setRemovingAssignment(false);
      return;
    }

    setRemovingAssignment(true);

    try {
      if (event.kind === 'workOrder') {
        const workOrderId = event?.original?.id || event?.original?.workOrderId;
        if (!workOrderId) throw new Error('Missing work order id');

        const workOrder = await getWorkOrderById(workOrderId);
        const existingCrew = safeArray(workOrder?.crew).map((item) => Number(typeof item === 'object' ? item?.id : item)).filter(Number.isFinite);
        const existingContractors = safeArray(workOrder?.contractors).map((item) => Number(typeof item === 'object' ? item?.id : item)).filter(Number.isFinite);

        const payload = assignee.type === 'internal'
          ? { crew: existingCrew.filter((id) => id !== Number(assignee.id)) }
          : { contractors: existingContractors.filter((id) => id !== Number(assignee.id)) };

        await updateWorkOrder(workOrderId, payload);
      } else {
        const formId = event?.original?.id || event?.original?.formId;
        if (!formId) throw new Error('Missing form id');

        const payload = assignee.type === 'internal'
          ? { assignUserId: null }
          : { assignedContractorId: null };

        await updateWorkOrderForm(formId, payload);
      }

      setPendingAssignmentRemoval(null);
      showToastSuccess('Assignment removed successfully');
      await fetchCalendarData();
    } catch (error) {
      console.error('Failed to remove assignment', error);
      showToastError('Failed to remove assignment');
    } finally {
      setRemovingAssignment(false);
    }
  };

  const assignedContractorName = previewForm?.assignedContractor?.name || previewForm?.assignedContractorName || '';
  const assignedContractorSupplierName = previewForm?.assignedContractor?.supplier?.name || '';
  const assignedContractorDisplay = assignedContractorName
    ? `${assignedContractorName}${assignedContractorSupplierName ? ` (${assignedContractorSupplierName})` : ''}`
    : '';
  const assignedUserDisplay = previewForm?.assignedUser?.name || '';
  const showAssignedContractorField = Boolean(assignedContractorDisplay);
  const showAssignedUserField = !showAssignedContractorField && Boolean(assignedUserDisplay);

  return (
    <>
      {headerSearchContainer && createPortal(
        <input
          className='form-control form-control-sm'
          placeholder='Search...'
          value={searchAssignee}
          onChange={(e) => setSearchAssignee(e.target.value)}
        />,
        headerSearchContainer,
      )}
      <div className='card h-100 p-0 rounded-0'>
        <div className='card-body p-3 d-flex flex-column gap-3'>
          <div className='d-flex flex-wrap gap-2 align-items-center'>
            {renderMenuBarFilters()}

            <button
              type='button'
              className='btn btn-sm btn-link text-primary p-0 mt-24 ms-16'
              onClick={() => resetFilters()}
            >
              Clear Filters
            </button>

            <div className='btn-group ms-auto'>
              <button
                type='button'
                className='btn text-primary border-0'
                onClick={() => openDrawer({}, quickAssignMode)}
              >
                <Icon
                  icon="mdi:share-variant-outline"
                  className="me-8"
                  style={{ position: "relative", top: "0px", fontSize: '24px' }}
                />
                <span style={{ position: "relative", top: "-1px" }}>Assign Work</span>
              </button>

              <button
                type='button'
                className='btn text-primary dropdown-toggle dropdown-toggle-split border-0'
                data-bs-toggle='dropdown'
                aria-expanded='false'
              >
                <span className='visually-hidden'>Toggle Dropdown</span>
              </button>

              <ul className='dropdown-menu dropdown-menu-end'>
                <li> <button type='button' className='dropdown-item' onClick={() => openDrawer({ lockKind: true, initialStep: 1 }, 'workOrder')}> Assign Work Order </button> </li> <li> <button type='button' className='dropdown-item' onClick={() => openDrawer({ lockKind: true, initialStep: 1 }, 'form')}> Assign Form </button> </li> <li> <button type='button' className='dropdown-item' onClick={() => openDrawer({ lockKind: true, initialStep: 1, preselectedTab: 'contractors' }, 'workOrder')}> Assign Work Order to Contractor </button> </li> <li> <button type='button' className='dropdown-item' onClick={() => openDrawer({ lockKind: true, initialStep: 1, preselectedTab: 'crew' }, 'workOrder')}> Assign Work Order to Crew </button> </li>
              </ul>
            </div>

            <div className='d-flex flex-wrap gap-2 align-items-center justify-content-end w-100 mt-2'>
              <div className='btn-group' role='group'>
                {Object.keys(RANGE_OPTIONS).map((mode) => (
                  <button key={mode} type='button' className={`btn btn-sm ${viewMode === mode ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setViewMode(mode)}>
                    {mode[0].toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>

              <div className='btn-group'>
                <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => shiftWindow(-1)}><Icon icon='mdi:chevron-left' className='ms-auto me-8 text-muted text-xl' /></button>
                <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => setWindowStart(startOfDay(new Date()))}>Today</button>
                <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => shiftWindow(1)}><Icon icon='mdi:chevron-right' className='ms-auto me-8 text-muted text-xl' /></button>
              </div>
            </div>
          </div>

          <div className='row g-2 flex-grow-1'>
            <div style={{ width: "250px" }}>
              <AssigneeList
                assignees={assigneeRows}
                collapsedGroups={listCollapsedGroups}
                onToggleGroup={(group) => setListCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }))}
                onToggleGroupVisibility={handleToggleGroupVisibility}
                visibilityMap={assigneeVisibility}
                onToggleVisibility={(assignee) => {
                  const key = `${assignee.type}:${assignee.id}`;
                  setAssigneeVisibility((prev) => ({ ...prev, [key]: !(prev[key] !== false) }));
                }}
              />
            </div>
            <div style={{ width: "calc(100% - 250px)" }}>
              {loading ? (
                <div className='text-muted'>Loading calendar...</div>
              ) : (
                <WorkAssignmentsCalendarGrid
                  days={days}
                  assigneeRows={{
                    internal: assigneeRows.flat.filter((item) => item.type === 'internal'),
                    contractor: assigneeRows.flat.filter((item) => item.type === 'contractor'),
                    unassigned: assigneeRows.flat.filter((item) => item.type === 'unassigned'),
                  }}
                  collapsedGroups={calendarCollapsedGroups}
                  onToggleGroup={(group) => setCalendarCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }))}
                  eventsByCell={eventsByCell}
                  gridRef={gridRef}
                  onCellClick={({ day, assignee }) => openDrawer({ date: day, assignee }, quickAssignMode)}
                  onChipClick={(event) => {
                    if (event.kind === 'workOrder' && event.assignees.length === 0) {
                      openDrawer({
                        kind: 'workOrder',
                        lockKind: true,
                        initialStep: 2,
                        date: event.date,
                        workOrderNumber: event?.original?.id || event?.original?.workOrderId,
                      }, 'workOrder');
                    } else if (event.kind === 'workOrder') {
                      handleOpenWorkOrderDetails(event);
                    } else {
                      handleOpenFormPreview(event);
                    }
                  }}
                  onChipDrop={handleChipDrop}
                  onChipRemove={handleRequestAssignmentRemoval}
                />
              )}
            </div>
          </div>
        </div>


        {pendingAssignmentRemoval && (
          <div className='modal show d-block' tabIndex='-1'>
            <div className='modal-dialog'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>Confirm Assignment Removal</h5>
                  <button type='button' className='btn-close' onClick={() => setPendingAssignmentRemoval(null)} disabled={removingAssignment} />
                </div>
                <div className='modal-body'>
                  This will remove <strong>{pendingAssignmentRemoval.assignee.name}</strong> from this <strong>{pendingAssignmentRemoval.event.kind === 'workOrder' ? 'Work Order' : 'Form'}</strong>. Do want you to confirm?
                </div>
                <div className='modal-footer'>
                  <button type='button' className='btn btn-secondary' onClick={() => setPendingAssignmentRemoval(null)} disabled={removingAssignment}>Cancel</button>
                  <button type='button' className='btn btn-primary' onClick={handleConfirmAssignmentRemoval} disabled={removingAssignment}>
                    {removingAssignment ? 'Updating...' : 'OK'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {pendingScheduleChange && (
          <div className='modal show d-block' tabIndex='-1'>
            <div className='modal-dialog'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>Confirm Schedule Change</h5>
                  <button type='button' className='btn-close' onClick={() => setPendingScheduleChange(null)} disabled={updatingSchedule} />
                </div>
                <div className='modal-body'>
                  Form / Work Order scheduled Date will be changed to <strong>{pendingScheduleChange.date}</strong>. Do you want to make the change?
                </div>
                <div className='modal-footer'>
                  <button type='button' className='btn btn-secondary' onClick={() => setPendingScheduleChange(null)} disabled={updatingSchedule}>Cancel</button>
                  <button type='button' className='btn btn-primary' onClick={handleConfirmScheduleChange} disabled={updatingSchedule}>
                    {updatingSchedule ? 'Updating...' : 'OK'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showWorkOrderDetailsModal && (
          <div className='modal modal-backdrop show d-block' tabIndex='-1'>
            <div className='modal-dialog modal-xl'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>
                    {workOrderDetailsLoading
                      ? 'Work Order Details'
                      : `${workOrderDetails?.workOrderNumber || ''} - ${workOrderDetails?.workOrderTitle || ''}`}
                  </h5>
                  <button type='button' className='btn-close' onClick={handleCloseWorkOrderDetails} />
                </div>
                <div className='modal-body'>
                  {workOrderDetailsLoading ? (
                    <div className='d-flex justify-content-center align-items-center py-4'>
                      <div className='spinner-border' role='status' aria-label='Loading work order details' />
                    </div>
                  ) : (
                    <>
                      <ul className='nav nav-tabs mb-3'>
                        <li className='nav-item'>
                          <button type='button' className={`nav-link ${workOrderPreviewTab === 'general' ? 'active' : ''}`} onClick={() => setWorkOrderPreviewTab('general')}>General</button>
                        </li>
                        <li className='nav-item'>
                          <button type='button' className={`nav-link ${workOrderPreviewTab === 'preview' ? 'active' : ''}`} onClick={() => setWorkOrderPreviewTab('preview')}>Preview</button>
                        </li>
                      </ul>

                      {workOrderPreviewTab === 'general' ? (
                        <div className='row g-2'>
                          {editWorkOrderFields.map((field) => <React.Fragment key={field.field}>{renderEditWorkOrderField(field)}</React.Fragment>)}
                          <div className='col-12 mt-3'>
                            {renderCommunicationOptions({
                              communication: workOrderCommunication,
                              kind: 'workOrder',
                              showSms: true,
                            })}
                          </div>
                          <div className='col-12 d-flex justify-content-end'>
                            <button type='button' className='btn btn-secondary me-12' onClick={handleCloseWorkOrderDetails} disabled={savingWorkOrderEdit}>
                              Cancel
                            </button>
                            <button type='button' className='btn btn-primary' onClick={handleSaveEditedWorkOrder} disabled={savingWorkOrderEdit}>
                              {savingWorkOrderEdit ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <WorkOrderDetails
                          workOrder={workOrderDetails}
                          parts={workOrderParts}
                          currentStatus={workOrderDetails.statusId}
                          labour={workOrderLabour}
                          services={workOrderServices}
                          equipment={workOrderEquipment}
                          forms={workOrderLinkedForms}
                          showAssignContractor={null}
                          showCurrentStatus={1}
                          crew={crew}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {showFormPreviewModal && (
          <div className='modal show d-block' tabIndex='-1'>
            <div className='modal-dialog modal-xl'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>{formPreviewLoading ? 'Form Details' : previewForm?.formName || 'Form Details'}</h5>
                  <button type='button' className='btn-close' onClick={handleCloseFormPreview} />
                </div>
                <div className='modal-body'>
                  {formPreviewLoading ? (
                    <div className='d-flex justify-content-center align-items-center py-4'>
                      <div className='spinner-border' role='status' aria-label='Loading form preview' />
                    </div>
                  ) : (
                    <>
                      <ul className='nav nav-tabs mb-3'>
                        <li className='nav-item'>
                          <button type='button' className={`nav-link ${formPreviewTab === 'general' ? 'active' : ''}`} onClick={() => setFormPreviewTab('general')}>General</button>
                        </li>
                        <li className='nav-item'>
                          <button type='button' className={`nav-link ${formPreviewTab === 'preview' ? 'active' : ''}`} onClick={() => setFormPreviewTab('preview')}>Preview</button>
                        </li>
                      </ul>
                      {formPreviewTab === 'general' ? (
                        <div className='row g-2'>
                          {showAssignedContractorField && (
                            <div className='col-md-6'>
                              <label className='form-label'>Assigned Contractor</label>
                              <div className='form-control d-flex align-items-center gap-2 disabled' style={{ minHeight: '38px' }} aria-disabled='true'>
                                {previewForm?.assignedContractor?.profileImage ? <img src={profileImageUrl(previewForm?.assignedContractor?.profileImage)} alt='' width='24' height='24' className='rounded-circle' /> : null}
                                <span>{assignedContractorDisplay}</span>
                              </div>
                            </div>
                          )}
                          {showAssignedUserField && (
                            <div className='col-md-6'>
                              <label className='form-label'>Assigned User</label>
                              <div className='form-control d-flex align-items-center gap-2 disabled' style={{ minHeight: '38px' }} aria-disabled='true'>
                                {previewForm?.assignedUser?.userImage ? <img src={profileImageUrl(previewForm?.assignedUser?.userImage)} alt='' width='24' height='24' className='rounded-circle' /> : null}
                                <span>{assignedUserDisplay}</span>
                              </div>
                            </div>
                          )}
                          {formGeneralFields.map((field) => <React.Fragment key={field.field}>{renderFormGeneralField(field)}</React.Fragment>)}
                          <div className='col-12 mt-3'>
                            {renderCommunicationOptions({
                              communication: formCommunication,
                              kind: 'form',
                              showSms: true,
                            })}
                          </div>
                          <div className='col-12 d-flex justify-content-end'>
                            <button type='button' className='btn btn-secondary me-12' onClick={handleCloseFormPreview} disabled={formGeneralSaving}>
                              Cancel
                            </button>
                            <button type='button' className='btn btn-primary' onClick={handleSaveFormGeneral} disabled={formGeneralSaving}>
                              {formGeneralSaving ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <FormLivePreview
                          form={previewForm?.formTemplate || previewForm}
                          items={safeArray(previewForm?.tblFormItems || previewForm?.items || previewForm?.formItems || previewForm?.formTemplate?.tblFormItems || previewForm?.formTemplate?.items)}
                          renderPreviewInput={renderPreviewInput}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <WorkAssignDrawer
          show={showDrawer}
          context={drawerContext}
          initialMode={quickAssignMode}
          onClose={() => setShowDrawer(false)}
          isSaving={savingAssignment}
          crewOptions={crew.filter((member) => Number(member?.assignable) !== 0).map((member) => ({
            id: member.id,
            name: member.name || member.email || `User ${member.id}`,
            email: member.email || '',
            phone: member.user_phone || member.phone || '',
            user_phone: member.user_phone || member.phone || '',
          }))}
          contractorOptions={contractors.map((member) => ({
            id: member.id,
            name: member.name || `Contractor ${member.id}`,
            supplierName: member?.supplier?.name || '',
            email: member?.email || '',
            phone: member?.phone || '',
            phoneE164: member?.phoneE164 || '',
            globalId: member?.globalId,
          }))}
          workOrderOptions={workOrders}
          formOptions={forms?._catalog || []}
          selectedDepartmentIds={selectedDepartmentIds}
          onConfirm={handleConfirmAssign}
        />
      </div>
    </>
  );
};

export default WorkAssignmentLayer;
